import React, { useState, useMemo } from 'react';
import { Box, Alert } from '@mui/material';
import { SuggestChangesButton } from './ImpcCodes.styled';
import { useImpcCodes } from '../../../common/hooks/useImpcCodes';
import Table from '../../../common/components/Table/Table';
import { Column } from 'react-table';

interface ImpcCode {
  id: number;
  ppo: string;
  impc: string;
  exclude: string;
}

const ImpcCodes: React.FC = () => {
  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortColumn, setSortColumn] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');
  
  // Search state
  const [searchColumn, setSearchColumn] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);

  // Fetch IMPC codes with pagination and search
  const { data, isLoading, isError, error } = useImpcCodes({
    page,
    pageSize,
    sortColumn,
    sortDirection,
    searchColumn,
    searchTerm,
    enabled: true
  });

  // Extract data and total count
  const impcCodesData = data?.data || [];
  const totalCount = data?.totalCount || 0;

  // Define table columns
  const columns: Column<ImpcCode>[] = useMemo(
    () => [
      {
        Header: 'DO',
        accessor: 'ppo',
        textAlign: 'center',
        width: '280px'
      },
      {
        Header: 'IMPC',
        accessor: 'impc',
        textAlign: 'center',
        width: '280px'
      },
      {
        Header: 'ETOE y/n',
        accessor: 'exclude',
        textAlign: 'center',
        width: '280px'
      }
    ],
    []
  );

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0); // Reset to first page when page size changes
  };

  // Handle sorting and searching - map frontend column names to API field names
  const handleSort = (
    column: string, 
    direction: 'ASC' | 'DESC' | undefined, 
    searchCol?: string, 
    searchValue?: string
  ) => {
    // Map frontend column accessor to API field name
    const columnMapping: Record<string, string> = {
      'ppo': 'exchangeOfficeName',
      'impc': 'name',
      'exclude': 'excludeFromUnex'
    };
    
    // Handle search
    if (searchCol && searchValue) {
      const apiSearchColumn = columnMapping[searchCol] || searchCol;
      setSearchColumn(apiSearchColumn);
      setSearchTerm(searchValue);
    } else if (searchCol && !searchValue) {
      // Clear search for this column
      setSearchColumn(undefined);
      setSearchTerm(undefined);
    }
    
    // Handle sorting
    if (direction) {
      const apiColumn = columnMapping[column] || 'name';
      setSortColumn(apiColumn);
      setSortDirection(direction);
    } else {
      // Reset to default sort
      setSortColumn('name');
      setSortDirection('ASC');
    }
    
    setPage(0); // Reset to first page when sorting/searching changes
  };

  return (
    <Box 
      data-testid="impc-codes-container"
      sx={{ 
        width: '100%',
        maxWidth: '900px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ mb: 3 }}>
        <SuggestChangesButton 
          href="mailto:ipep@ipc.be"
          data-testid="suggest-changes-button"
        >
          SUGGEST CHANGES
        </SuggestChangesButton>
      </Box>
      
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        width: '100%',
        flex: 1,
        overflow: 'auto'
      }}>
        {isError ? (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="impc-codes-error">
            {error instanceof Error ? error.message : 'Failed to load IMPC codes. Please try again later.'}
          </Alert>
        ) : impcCodesData.length === 0 && !isLoading ? (
          <Alert severity="info" sx={{ mb: 2 }} data-testid="impc-codes-empty">
            No IMPC codes found.
          </Alert>
        ) : (
          <Table<ImpcCode>
            data={impcCodesData}
            columns={columns}
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onSort={handleSort}
            isLoading={isLoading}
            moduleName="IMPC Codes"
            viewMode={true}
            showPagination={true}
            enableSearch={true}
            disableAutoSizing={true}
          />
        )}
      </Box>
    </Box>
  );
};

export default ImpcCodes; 